import { Component } from '@angular/core';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;


/*
 * data-layer-geojson-point-links defines a class to display geojson point data.  Additional links specified display in the
 * 	popup for the point.
 *
 * Example of the data file:
 * --------------------------------
 * {
 *	"type": "FeatureCollection",
 *	"features": [
 *	  {
 *		"type": "Feature",
 *		"properties": {
 *			"data_source": "WETMap",
 *			"name": "Station ABC",
 *			"link": "http://123.34.5.66/path/to/something",
 *			"addnlLinks": "[{'link1',http://123.34.5.66/path/to/something/else"},{'link2',http://123.34.5.66/another/path}]
 *		},
 *		"geometry": {
 *		  "type": "Point",
 *		  "coordinates": [-105.07477, 40.603510]
 *		}
 *	  },
 *	  ...
 *	]
 * }
 * --------------------------------
 *
 * Within the configuration file, point stations can be included:
 * "overlayLayers": [
 * 		{"id": "pointLayer", "label": "Points", "url": "map/overlay-layers/cameraStations.geojson",
 *		 "class": "data-layer-geojson-point-links", "refresh": false },
 * ...
 * ]
 */

@Component({
	selector: 'data-layer-geojson-point-links',
	template: `
		<div class="checkboxDiv"><label class="overlay-layer">
		<input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPointLinksComponent extends DataLayerGeojsonPointComponent {

	color = 'red';

	constructor(mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService ) {
		super(mapService, dataLayerService, configService, userMessageService);
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}
	}

	setData ( someData: any ) {
		super.setData(someData);
		if ( someData.hasOwnProperty('color')) {
			this.color = someData.color;
		}
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// first check whether the information has been read.
	// display the markers on the map and set the layer to active
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.mapLayer != null ) {
				this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established
			this.parseDataFile();
		} else {
			this.drawMarkers();
		}

		this.active = true;
	}

	drawMarkers () {
		console.log('Drawing ' + this.value.features.length + ' stations.');
		const self = this;
		this.mapLayer = L.geoJson(this.value, {
			// placeMarkers using configuration which specifies which property should be used as the title, popup, etc.
			pointToLayer: this.placeMarkers,
			onEachFeature: this.onEachPointFeature
		}, this).addTo(this.mapService.map);
	}

	// Create the popup and tooltip.
	onEachPointFeature(feature, layer) {
		let popupContent, tooltipContent, options;

		const stationName = feature.properties['name'] != null ? feature.properties['name'] : '';
		const stationDescription = feature.properties['description'] != null ? feature.properties['description'] : '';
		const linkStr = 'link';
		const height = 'image_height';
		const heightStr = feature.properties[height] != null ? 'height:' + feature.properties[height] + 'px' : '';
		const width = 'image_width';
		const widthStr = feature.properties[width] != null ? 'width:' + feature.properties[width] + 'px' : '';
		const styleString = (heightStr + widthStr).length > 0 ? 'style=' + heightStr + '; ' + widthStr : '';
		const imageStr = 'image';
		const fullImageStr = feature.properties[imageStr] != null ?
			'<img src=\'' + feature.properties[imageStr] + '\'' + styleString  + '\'>' : '';

		// create the additional links string
		let addnlLinksStr = '';
		const addnlLinks = feature.properties['addnlLinks'] != null ? feature.properties['addnlLinks'] : [];
		// console.log(addnlLinks);
		// console.log(addnlLinks.length);
		for ( let i = 0; i < addnlLinks.length; i++ ) {
			// console.log(addnlLinks[i]);
			addnlLinksStr += '<br><a href=\'' + addnlLinks[i].link + '\' target=\'_blank\'> ' + addnlLinks[i].name + '</a>';
		}
		// console.log(addnlLinksStr);


		// Assign HTML content to use in a popup when the user clicks on a marker

		popupContent = '<p style=\'font-size:20px\'>' + stationName +
			' - ' + stationDescription + fullImageStr +
			'<br><a href=\'' + feature.properties[linkStr] + '\' target=\'_blank\'>Click for more information: ' +
			stationName + '</a>' + addnlLinksStr;

		options = { 'minWidth': feature.properties[width] != null ? feature.properties[width] : 60 };
		tooltipContent = stationName + ' (click for more info)';

		if (feature.properties && feature.properties.popupContent) {
			// This will append content from a "popupContent" property if it is in the GeoJSON file
			popupContent += feature.properties.popupContent;
		}

		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent);
	}

	// extended classes should implement this function
	placeMarkers(feature, latlng) {
		// var self = this;
		// console.log(this); // undefined
		let marker;
		let title = '';
		if ( feature.properties.id != null ) {
			title = feature.properties.id;
		} else if ( feature.properties.name != null ) {
			title = feature.properties.name;
							}

		marker = L.ExtraMarkers.icon({
			icon: 'fa-number',
			iconColor: 'white',		// text color
			markerColor: 'blue',	// marker background color
			shape: 'circle',
			prefix: 'fa',
			number: title
		}, this);

		marker.options.shadowSize = [50, 75];

		return L.marker(latlng, {icon: marker});
	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			this.active = false;
		}

	}

	// from data-layer-service.parseCameraStations
	parseDataFile () {
		console.log('In data-layer-geojson-point.parseDataFile.');
		const self = this;

		const requestStations = new XMLHttpRequest();
		requestStations.onload = function ( ) {
			if ( requestStations.readyState === requestStations.DONE && requestStations.status === 200 ) {

				// first, get json file content
				const allStations = JSON.parse(requestStations.responseText);
				// console.log(allStations);


				self.value = allStations;
				self.valueObservable.next(allStations);
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
					filename + ' for all ' + self.id + ' stations was successfully completed.');
				// console.log ( dataLayer );

			} else {
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + filename + ' for all ' + self.id + ' stations was UNSUCCESSFUL.');
			}
		};
		const filename = this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
		requestStations.open('get', filename, true );
		requestStations.overrideMimeType('text/plain');
		console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id + ' stations.');
		requestStations.send();
	}

}
